import { Box, FormControlLabel, Grid, Radio, TextField } from "@mui/material";
import * as React from "react";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../../framework/appUtils";
import { SaveDataObj } from "../../../../../framework/saveData";
import * as documentService from "../../../../../models/services/documentService";
import { CkEditorDialog } from "../../../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../../../framework/Dialog/DialogItemsNav";
import { FormField } from "../../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { IDocumentTemplateEditItem } from "source/models/documentTemplate/documentTemplateEditItem";
import { AppCkEditor } from "source/components/framework/AppCkEditor";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

export interface IDocumentTemplateDialogProp {
    classes?: string;
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onOk: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (ids: string[]) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

export interface IDocumentTemplateDialogState {
    id: string;
    rowId: string;
    isNew: boolean;
    code: string;
    comment: string;
    content: string;
    landscapeOrientation: boolean;

    item: IDocumentTemplateEditItem;
    ckEditorDataId: number;
}

export class DocumentTemplateDialog extends React.Component<IDocumentTemplateDialogProp, IDocumentTemplateDialogState> {
    private orgStateHash: string = "";
    private editor: any = null;

    constructor(props: IDocumentTemplateDialogProp) {
        super(props);
        this.state = {
            item: null,
            id: "",
            rowId: "",
            isNew: false,
            code: "",
            comment: "",
            content: "",
            landscapeOrientation: false,
            ckEditorDataId: 0
        };
    }

    // #region CkEditor
    setCkEditorContent = (value: string) => {
        this.setState({ ckEditorDataId: this.state.ckEditorDataId + 1 });
        if (!this.editor) return;
        this.editor.setData(value, {
            callback: () => {
                this.editor.resetDirty();
                this.orgStateHash = this.getHashFromState(this.props, this.state);
            }
        });
        this.setState({ ckEditorDataId: this.state.ckEditorDataId + 1 });
    };

    destroyCkEditor = () => {
        if (!this.editor) return;
        this.editor.destroy();
        this.editor = null;
    };

    handleCkEditorInstanceReady = (ev) => {
        this.editor = ev.editor;
        ev.editor.resetDirty();
        this.setCkEditorContent(this.state.content);
    };
    // #endregion CkEditor

    loadData = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => documentService.getDocumentTemplateEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.item;
        const state = {
            ...this.state,
            item,
            id: item.id,
            rowId: item.rowId,
            isNew: item.isNew(),
            code: item.code,
            comment: item.comment,
            content: item.content,
            landscapeOrientation: item.landscapeOrientation
        };
        this.setState(state, () => {
            this.setCkEditorContent(item.content);
        });
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }
    
    componentWillUnmount(): void {
        this.destroyCkEditor();
    }

    async componentDidUpdate(prevProps: IDocumentTemplateDialogProp, prevState: IDocumentTemplateDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(props.id);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    getHashFromState = (props: IDocumentTemplateDialogProp, state: IDocumentTemplateDialogState): string => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("landscapeOrientation", state.landscapeOrientation);
        let contentDirty = false;
        if (this.editor) {
            contentDirty = this.editor.checkDirty();
        }
        result.append("changed", contentDirty);
        return result.hash;
    };

    getSaveDataFromState = (props: IDocumentTemplateDialogProp, state: IDocumentTemplateDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("comment", state.comment);
        result.append("landscapeOrientation", state.landscapeOrientation);
        let content = state.content;
        if (this.editor) {
            content = this.editor.getData();
        }
        result.append("content", content);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                AppUtils.showErrorMessage(Translations.NameMustBeDefined);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => documentService.saveDocumentTemplateEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        if (this.editor) {
            this.editor.resetDirty();
        }
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = this.getHashFromState(props, { ...state, ...stateChange });
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onOk();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveDocumentTemplateParameter, state.item.code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove([state.id]);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <CkEditorDialog
                id="documentTemplateDialog"
                maxWidth="lg"
                maxHeight="90%"
                title={state.isNew
                    ? (props.companyId ? Translations.AddDocumentTemplate : Translations.AddSharedDocumentTemplate)
                    : ((props.companyId ? Translations.DocumentTemplate : Translations.SharedDocumentTemplate) + ": " + state.item.code)}
                body={
                    <VerticalContainer
                        before={<>
                            <GridRow>
                                <Grid item sm={4}>
                                    <FormField title={Translations.Name} fullWidth required margin="0">
                                        <TextField name="code" title={Translations.Name} inputProps={{ maxLength: 50 }} value={state.code} required={true} onChange={this.handleChange} size="small" fullWidth />
                                    </FormField>
                                </Grid>
                                <Grid item sm={5}>
                                    <FormField title={Translations.Description} fullWidth margin="0">
                                        <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} value={state.comment} onChange={this.handleChange} size="small" fullWidth />
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.Orientation} fullWidth margin="0">
                                        <div style={{ display: "flex" }}>
                                            <FormControlLabel
                                                label={Translations.Portrait}
                                                control={
                                                    <Radio
                                                        checked={!state.landscapeOrientation}
                                                        onChange={(_e, checked: boolean) => { this.setState({ landscapeOrientation: !checked }); }}
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label={Translations.Landscape}
                                                control={
                                                    <Radio
                                                        checked={state.landscapeOrientation}
                                                        onChange={(_e, checked: boolean) => { this.setState({ landscapeOrientation: checked }); }}
                                                    />
                                                }
                                            />
                                        </div>
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Contents} fullWidth>
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </>}
                        main={
                            <AppCkEditor
                                readOnly={false}
                                value={state.content}
                                dataId={state.ckEditorDataId}
                                fitToContainerHeight={true}
                                height=""
                                documentLandscapeOrientation={state.landscapeOrientation}
                                onInstanceReady={this.handleCkEditorInstanceReady}
                                onChange={null}
                            />
                        }
                    />
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!state.isNew ? state.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={state.isNew}
                            onCancel={this.handleCancel}
                            onRemove={!state.isNew ? this.handleRemove : null}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}